import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { RequireAuth } from "@/components/auth/RequireAuth";

// Helper-компоненты для legacy /app/* редиректов с параметрами
function LegacyChatRedirect() {
  const { id } = useParams();
  return <Navigate to={`/chat/${id}`} replace />;
}
function LegacyGenerateRedirect() {
  const { category, slug } = useParams();
  return <Navigate to={`/generate/${category}${slug ? `/${slug}` : ""}`} replace />;
}

const LandingPage          = lazy(() => import("@/pages/public/LandingPage"));
const ModelLandingPage     = lazy(() => import("@/pages/public/ModelLandingPage"));
const ResetPasswordPage    = lazy(() => import("@/pages/auth/ResetPasswordPage"));
const ConfirmEmailPage     = lazy(() => import("@/pages/auth/ConfirmEmailPage"));
const NewChatPage          = lazy(() => import("@/pages/chat/NewChatPage"));
const ChatSessionPage      = lazy(() => import("@/pages/chat/ChatSessionPage"));
const ChatHistoryPage      = lazy(() => import("@/pages/chat/ChatHistoryPage"));
const ModelsCatalogPage    = lazy(() => import("@/pages/models/ModelsCatalogPage"));
const FilesPage            = lazy(() => import("@/pages/files/FilesPage"));
const MonitoringPage       = lazy(() => import("@/pages/monitoring/MonitoringPage"));
const BillingPage          = lazy(() => import("@/pages/billing/BillingPage"));
const SettingsPage         = lazy(() => import("@/pages/settings/SettingsPage"));
const GeneratePage         = lazy(() => import("@/pages/generate/GeneratePage"));
// Admin shell + 8 pages (TZ §10)
const AdminDashboardPage   = lazy(() => import("@/pages/admin/DashboardPage"));
const AdminUsersPage       = lazy(() => import("@/pages/admin/UsersPage"));
const AdminModelsPage      = lazy(() => import("@/pages/admin/ModelsPage"));
const AdminTemplatesPage   = lazy(() => import("@/pages/admin/TemplatesPage"));
const AdminCreditPacksPage = lazy(() => import("@/pages/admin/CreditPacksPage"));
const AdminPromosPage      = lazy(() => import("@/pages/admin/PromosPage"));
const AdminReferralsPage   = lazy(() => import("@/pages/admin/ReferralsPage"));
const AdminTicketsPage     = lazy(() => import("@/pages/admin/TicketsPage"));
const AdminErrorsPage      = lazy(() => import("@/pages/admin/ErrorsPage"));
const AdminPaymentsPage    = lazy(() => import("@/pages/admin/PaymentsPage"));
// Public static pages (TZ §12) - все 7 идут одним wrapper-модулем
import {
  FaqPage, HelpPage, ContactsPage, TermsPage,
  PrivacyPage, RefundsPage, CreditsPolicyPage,
} from "@/pages/public/StaticRoutes";
import { AdminLayout } from "@/components/admin/AdminLayout";

function PageFallback() {
  return (
    <div className="grid place-items-center h-[60vh] text-ink-soft text-sm">
      Загрузка…
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        {/* Public SEO landings per model (TZ §11) */}
        <Route path="/models/:slug"   element={<ModelLandingPage />} />
        {/* Static pages (TZ §12) */}
        <Route path="/faq"            element={<FaqPage />} />
        <Route path="/help"           element={<HelpPage />} />
        <Route path="/contacts"       element={<ContactsPage />} />
        <Route path="/terms"          element={<TermsPage />} />
        <Route path="/privacy"        element={<PrivacyPage />} />
        <Route path="/refunds"        element={<RefundsPage />} />
        <Route path="/credits-policy" element={<CreditsPolicyPage />} />
        {/* Auth flows landing pages (followed from email links) */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/confirm-email"  element={<ConfirmEmailPage />} />
        {/* Защищённые страницы доступны только авторизованным. /app/
            префикс убран - юзеры теперь видят /chat/new, /models, /files
            и т.п. Legacy /app/* редиректится на новые URL. */}
        <Route element={<RequireAuth />}>
          <Route element={<AppLayout />}>
            <Route path="/chat/new"    element={<NewChatPage />} />
            <Route path="/chat/:id"    element={<ChatSessionPage />} />
            <Route path="/chats"       element={<ChatHistoryPage />} />
            {/* /models/:slug - публичный SEO-лендинг (определён выше);
                /models без slug - каталог в кабинете. */}
            <Route path="/catalog"     element={<ModelsCatalogPage />} />
            <Route path="/generate/:category"        element={<GeneratePage />} />
            <Route path="/generate/:category/:slug"  element={<GeneratePage />} />
            <Route path="/files"       element={<FilesPage />} />
            <Route path="/monitoring"  element={<MonitoringPage />} />
            <Route path="/billing"     element={<BillingPage />} />
            <Route path="/settings"    element={<SettingsPage />} />
          </Route>
        </Route>
        {/* Legacy redirects: /app/* → /* (старые закладки и письма). */}
        <Route path="/app"              element={<Navigate to="/chat/new" replace />} />
        <Route path="/app/chat/new"     element={<Navigate to="/chat/new" replace />} />
        <Route path="/app/chats"        element={<Navigate to="/chats" replace />} />
        <Route path="/app/models"       element={<Navigate to="/catalog" replace />} />
        <Route path="/app/files"        element={<Navigate to="/files" replace />} />
        <Route path="/app/monitoring"   element={<Navigate to="/monitoring" replace />} />
        <Route path="/app/billing"      element={<Navigate to="/billing" replace />} />
        <Route path="/app/settings"     element={<Navigate to="/settings" replace />} />
        <Route path="/app/chat/:id"     element={<LegacyChatRedirect />} />
        <Route path="/app/generate/:category"        element={<LegacyGenerateRedirect />} />
        <Route path="/app/generate/:category/:slug"  element={<LegacyGenerateRedirect />} />
        {/* /admin/* - только для role=admin. RequireAuth(admin) - юзер,
            не админ, тихо редиректится на «/». */}
        <Route element={<RequireAuth admin />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index                element={<AdminDashboardPage />} />
            <Route path="users"         element={<AdminUsersPage />} />
            <Route path="models"        element={<AdminModelsPage />} />
            <Route path="templates"     element={<AdminTemplatesPage />} />
            <Route path="credit-packs"  element={<AdminCreditPacksPage />} />
            <Route path="promos"        element={<AdminPromosPage />} />
            <Route path="referrals"     element={<AdminReferralsPage />} />
            <Route path="tickets"       element={<AdminTicketsPage />} />
            <Route path="errors"        element={<AdminErrorsPage />} />
            <Route path="payments"      element={<AdminPaymentsPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
